// gbp.ts — Google Business Profile connect + management API methods.
// See workers/core-os/src/routes/gbp.ts. Every call degrades cleanly: until
// Google grants Business Profile API access (quota 0 -> 300) and a location is
// selected, status reports the state and the management calls return ok:false.

import { request } from './request'

export interface GbpAccount {
  name: string
  accountName: string
}

export interface GbpStatusResponse {
  ok: true
  connected: boolean
  token_valid?: boolean
  account_email?: string | null
  approved?: boolean
  accounts?: GbpAccount[]
  reason?: string
  location?: {
    account_name: string | null
    location_name: string | null
    location_title: string | null
  } | null
}

export interface GbpLocationOption {
  account_name: string
  account_label: string
  location_name: string
  location_title: string
}

export interface GbpReview {
  name: string
  reviewer: string | null
  stars: number
  comment: string | null
  create_time: string | null
  existing_reply: string | null
  draft: string | null
  draft_error: string | null
}

export const gbpApi = {
  status: () => request<GbpStatusResponse>('/api/integrations/gbp/status'),

  connect: () =>
    request<{ ok: boolean; url?: string; state?: string; error?: string; detail?: string }>(
      '/api/integrations/gbp/connect',
    ),

  locations: () =>
    request<{
      ok: boolean
      locations?: GbpLocationOption[]
      count?: number
      error?: string
      detail?: string
    }>('/api/integrations/gbp/locations'),

  selectLocation: (body: {
    account_name: string
    location_name: string
    location_title?: string | null
  }) =>
    request<{ ok: boolean; error?: string; detail?: string }>(
      '/api/integrations/gbp/select-location',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  disconnect: () =>
    request<{ ok: boolean; disconnected?: boolean; error?: string }>(
      '/api/integrations/gbp/disconnect',
      { method: 'POST' },
    ),

  reviews: () =>
    request<{
      ok: boolean
      location_title?: string | null
      count?: number
      drafted?: number
      reviews?: GbpReview[]
      error?: string
      detail?: string
    }>('/api/integrations/gbp/reviews'),

  replyToReview: (body: { review_name: string; comment: string }) =>
    request<{ ok: boolean; error?: string; detail?: string }>(
      '/api/integrations/gbp/reviews/reply',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  performance: () =>
    request<{
      ok: boolean
      range?: { start: string; end: string }
      totals?: Record<string, number>
      raw?: unknown
      error?: string
      detail?: string
    }>('/api/integrations/gbp/performance'),

  post: (body?: { published_content_id?: string }) =>
    request<{
      ran: boolean
      reason?: string
      posted?: boolean
      published_content_id?: string
      post_name?: string
      error?: string
      ambiguous?: boolean
    }>('/api/integrations/gbp/post', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
}
